import {
  Box,
  Heading,
  Image,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Flex,
  useToast,
  Spinner,
  Checkbox,
  Button,
  Badge,
  Text,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Select,
} from "@chakra-ui/react";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import CodesetSelect from "./CodesetSelect";
import UuidPreview from "./UuidPreview";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
  HiEye,
  HiOutlineTrash,
  HiOutlineUpload,
} from "react-icons/hi";
import { MdClose } from "react-icons/md";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import { useAuthenticatedRequest } from "./hooks/useAuthenticatedRequest";
import { useAuth } from "./AuthContext";
import { canEditRecord, withOwnerOrganisation } from "./utils/ownership";
import { errorFromResponse } from "./utils/apiErrors";

const RegistersEditor = ({ register, onClose, isNew }) => {
  const [registerHeader, setRegisterHeader] = useState({});
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [importFormData, setImportFormData] = useState({
    collection: "",
    type: "",
  });
  const [codifySpecifications, setCodifySpecifications] = useState([]);
  const [codifyDefinitions, setCodifyDefinitions] = useState([]);
  const [selectedCodifySpec, setSelectedCodifySpec] = useState("");
  const [selectedSpecification, setSelectedSpecification] = useState(null);
  const [selectedDefinition, setSelectedDefinition] = useState("");
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const navigate = useNavigate();
  const toast = useToast();
  const { get, post, put, delete: del, getHeaders } = useAuthenticatedRequest();
  const { user } = useAuth();
  const isReadOnly = !isNew && !canEditRecord(user, register);

  useEffect(() => {
    if (register) {
      setRegisterHeader({ ...register });
    }
  }, [register]);

  // Fetch all codify specifications on mount
  useEffect(() => {
    const fetchAllSpecifications = async () => {
      try {
        const response = await get("https://codify.poc.mrc.ai/api/specifications/headers");
        setCodifySpecifications(response.data);
      } catch {
        toast({
          title: "Error fetching codify specifications",
          status: "error",
          duration: 3000,
        });
      }
    };

    fetchAllSpecifications();
  }, [toast]);

  // Fetch codify definitions when a collection is selected
  useEffect(() => {
    const fetchDefinitions = async () => {
      if (importFormData.collection) {
        try {
          const response = await get(
            `https://codify.poc.mrc.ai/api/definitions/headers?collection=${importFormData.collection}`
          );
          setCodifyDefinitions(response.data);
        } catch (error) {
          console.error("Error fetching definitions:", error);
          toast({
            title: "Error fetching definitions for collection",
            status: "error",
            duration: 3000,
          });
        }
      } else {
        setCodifyDefinitions([]);
      }
    };

    fetchDefinitions();
  }, [importFormData.collection, toast]);

  // Fetch entries separately from new API
  useEffect(() => {
    const fetchEntries = async () => {
      if (!register || isNew) {
        setEntries([]);
        return;
      }

      setEntriesLoading(true);
      try {
        const response = await get(
          `https://registers.poc.mrc.ai/api/registerentries/headers?registerId=${register._id}`
        );
        setEntries(response.data);
      } catch (error) {
        console.error("Error fetching entries:", error);
        toast({
          title: "Failed to load entries",
          description: error.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setEntriesLoading(false);
      }
    };

    fetchEntries();
  }, [register, isNew, toast]);

  const handleHeaderChange = (field, value) => {
    setRegisterHeader((prev) => {
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
  };

  const handleSave = async () => {
    try {
      // Save only register header (no entries)
      console.log("Saving register with data:", JSON.stringify(registerHeader, null, 2));
      const url = isNew
        ? "https://registers.poc.mrc.ai/api/registers"
        : `https://registers.poc.mrc.ai/api/registers/${registerHeader._id}`;

      const response = isNew
        ? await post(url, withOwnerOrganisation(registerHeader, user))
        : await put(url, registerHeader);

      const result = response.data;

      toast({
        title: isNew ? "Register created" : "Register saved",
        description: isNew
          ? "New register was added."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew && result._id) {
        navigate(`/registers/${result._id}`);
      }
    } catch (error) {
      console.error("❌ Error saving register:", error);

      toast({
        title: "Save failed",
        description:
          error.message || "An error occurred while saving the register.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await del(
        `https://registers.poc.mrc.ai/api/registerentries/${entryId}`
      );

      setEntries((prev) => prev.filter((e) => e._id !== entryId));

      toast({
        title: "Entry deleted",
        description: "Register entry was deleted successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("❌ Error deleting entry:", error);
      toast({
        title: "Delete failed",
        description: error.message || "An error occurred while deleting the entry.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleAddEntry = () => {
    if (!registerHeader._id) {
      toast({
        title: "Cannot add entry",
        description: "Please save the register first before adding entries.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    navigate(`/registers/${registerHeader._id}/entry/new`);
  };

  const handleEditEntry = (entryId) => {
    navigate(`/registers/${registerHeader._id}/entry/${entryId}`);
  };

  const validateFile = (file) => {
    const validExtensions = [
      ".txt",
      ".text",
      ".pdf",
      ".docx",
      ".doc",
      ".xlsx",
      ".xls",
      ".pptx",
      ".ppt",
      ".json",
      ".xml",
      ".csv",
    ];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      toast({
        title: "Invalid file type",
        description: `Please upload one of: ${validExtensions.join(", ")}`,
        status: "error",
        duration: 5000,
      });
      return false;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: "Maximum file size is 10MB",
        status: "error",
        duration: 5000,
      });
      return false;
    }

    return true;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === dropZoneRef.current) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCodifySpecChange = async (e) => {
    const specId = e.target.value;
    setSelectedCodifySpec(specId);
    setSelectedDefinition("");

    if (specId) {
      // The list holds header objects; the import needs the full specification
      // (dataStandard, dataType, model)
      let selectedSpec = codifySpecifications.find((spec) => spec._id === specId);
      try {
        const response = await get(`https://codify.poc.mrc.ai/api/specifications/${specId}`);
        selectedSpec = response.data;
      } catch (error) {
        toast({
          title: "Error loading codify specification",
          description: error.message,
          status: "error",
          duration: 3000,
        });
        selectedSpec = null;
        setSelectedSpecification(null);
      }
      if (selectedSpec) {
        setSelectedSpecification(selectedSpec);
        const collection = selectedSpec.codifyCollection || selectedSpec.collection || "";
        setImportFormData({
          collection: collection,
          type: "",
        });
      }
    } else {
      setSelectedSpecification(null);
      setImportFormData({
        collection: "",
        type: "",
      });
    }
  };

  const handleDefinitionChange = (e) => {
    const defId = e.target.value;
    setSelectedDefinition(defId);

    if (defId) {
      const selectedDef = codifyDefinitions.find((def) => def._id === defId);
      if (selectedDef) {
        // Set the type from the selected definition
        setImportFormData((prev) => ({
          ...prev,
          type: selectedDef.type || "",
        }));
      }
    } else {
      setImportFormData((prev) => ({
        ...prev,
        type: "",
      }));
    }
  };

  const handleImportFile = async () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select a file to import",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (!registerHeader._id) {
      toast({
        title: "Cannot import",
        description: "Please save the register first before importing files.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (!selectedCodifySpec) {
      toast({
        title: "No specification selected",
        description: "Please select a Codify Specification",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (!selectedDefinition || !importFormData.type) {
      toast({
        title: "No definition selected",
        description: "Please select a Definition",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setIsImporting(true);
    setImportProgress({ status: "uploading", message: "Uploading and processing file..." });

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      // Build query string with only the fields needed for codification
      const queryParamsObj = {
        collection: selectedSpecification.codifyCollection || selectedSpecification.collection,
        type: importFormData.type,
        outputDataStandard: selectedSpecification.dataStandard,
        outputDataType: selectedSpecification.dataType,
        model: selectedSpecification.model,
      };

      const queryParams = new URLSearchParams(queryParamsObj);

      const authHeaders = getHeaders();
      const response = await fetch(
        `https://registers.poc.mrc.ai/api/registers/${registerHeader._id}/import?${queryParams}`,
        {
          method: "POST",
          headers: { 'Authorization': authHeaders['Authorization'] },
          body: formData,
        }
      );

      if (!response.ok) {
        throw await errorFromResponse(response, `Import failed (HTTP ${response.status})`);
      }

      const result = await response.json();

      toast({
        title: "Import successful",
        description: `Successfully imported ${selectedFile.name}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setImportProgress({
        status: "completed",
        message: "Import completed successfully. Refreshing entries..."
      });

      // Refresh entries after successful import
      setEntriesLoading(true);
      try {
        const entriesResponse = await get(
          `https://registers.poc.mrc.ai/api/registerentries/headers?registerId=${registerHeader._id}`
        );
        setEntries(entriesResponse.data);
      } catch (error) {
        console.error("Error refreshing entries:", error);
      } finally {
        setEntriesLoading(false);
      }

      // Clear file selection
      handleRemoveFile();
      setImportProgress(null);
    } catch (error) {
      console.error("❌ Error importing file:", error);
      toast({
        title: "Import failed",
        description: error.message || "An error occurred while importing the file.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setImportProgress({
        status: "error",
        message: error.message || "Import failed"
      });
      setTimeout(() => setImportProgress(null), 3000);
    } finally {
      setIsImporting(false);
    }
  };

  // ag-Grid column definitions
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Edit",
        field: "_id",
        flex: 0.5,
        cellRenderer: (params) => {
          return (
            <HStack spacing={2} height="100%" alignItems="center">
              <Tooltip label="Edit Entry">
                <IconButton
                  icon={<HiEye />}
                  size="sm"
                  onClick={() => handleEditEntry(params.value)}
                  aria-label="Edit entry"
                />
              </Tooltip>
            </HStack>
          );
        },
      },
      {
        headerName: "Name",
        field: "name",
        flex: 2,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Type",
        field: "type",
        flex: 1,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Is Definition",
        field: "isDefinition",
        flex: 0.75,
        sortable: true,
        filter: true,
        cellRenderer: (params) => {
          return params.value ? "Yes" : "No";
        },
      },
      {
        headerName: "Description",
        field: "description",
        flex: 2,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Delete",
        field: "_id",
        flex: 0.5,
        cellRenderer: (params) => {
          return (
            <HStack spacing={2} height="100%" alignItems="center">
              <Tooltip label="Delete Entry">
                <IconButton
                  icon={<HiOutlineTrash />}
                  size="sm"
                  onClick={() => handleDeleteEntry(params.value)}
                  isDisabled={!canEditRecord(user, params.data)}
                  aria-label="Delete entry"
                />
              </Tooltip>
            </HStack>
          );
        },
      },
    ],
    [user]
  );

  const handleClose = () => {
    navigate("/Poc");
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back To Environment tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <Heading size="lg">Edit Register</Heading>
                <Tooltip
                  label={isReadOnly ? "Read-only: owned by another organisation" : "Save Register"}
                  aria-label="Save Register Tooltip">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                    isDisabled={isReadOnly}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <VStack spacing={6} align="stretch">
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={4}
              borderColor="gray.500"
              backgroundColor="whiteAlpha.800">
              <UuidPreview uuid={registerHeader._id} />
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={registerHeader.name || ""}
                  onChange={(e) => handleHeaderChange("name", e.target.value)}
                />
              </FormControl>

              <FormControl mt={4}>
                <Checkbox
                  isChecked={registerHeader.isDefinitionLibrary || false}
                  onChange={(e) =>
                    setRegisterHeader((prev) => ({
                      ...prev,
                      isDefinitionLibrary: e.target.checked,
                    }))
                  }
                >
                  Is Definition Library
                </Checkbox>
              </FormControl>

              <CodesetSelect
                label="Type"
                codesetCode="B57"
                value={
                  registerHeader.type
                    ? { code: registerHeader.type, description: "" }
                    : null
                }
                onChange={(val) => handleHeaderChange("type", val.code)}
              />

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  value={registerHeader.description || ""}
                  onChange={(e) =>
                    handleHeaderChange("description", e.target.value)
                  }
                />
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>As Of Date</FormLabel>
                <Input
                  value={registerHeader.asOfDateTime || ""}
                  onChange={(e) =>
                    handleHeaderChange("asOfDateTime", e.target.value)
                  }
                />
              </FormControl>
            </Box>

            <Box
              borderWidth="1px"
              borderRadius="md"
              p={4}
              borderColor="gray.500"
              backgroundColor="whiteAlpha.800">
              <HStack alignItems="center" mb={4}>
                <Heading size="md">Entries</Heading>
                <Tooltip label="Add Entry">
                  <IconButton
                    icon={<HiOutlinePlus />}
                    size="md"
                    onClick={handleAddEntry}
                    aria-label="Add entry"
                  />
                </Tooltip>
              </HStack>

              {/* File Import Section */}
              <Box mb={4} p={4} borderWidth="1px" borderRadius="md" borderColor="gray.300" backgroundColor="gray.50">
                <Heading size="sm" mb={3}>Import File</Heading>
                <VStack spacing={3} align="stretch">
                  {/* Codify Specification and Definition Selectors */}
                  <HStack spacing={3} align="flex-start">
                    <FormControl>
                      <FormLabel>Codify Specification</FormLabel>
                      <Select
                        size="sm"
                        value={selectedCodifySpec}
                        onChange={handleCodifySpecChange}
                        placeholder="Select a codify specification...">
                        {codifySpecifications
                          .sort((a, b) => {
                            const nameCompare = (a.name || a.collection).localeCompare(b.name || b.collection);
                            return nameCompare;
                          })
                          .map((spec) => (
                            <option key={spec._id} value={spec._id}>
                              {spec.name || spec.collection}
                            </option>
                          ))}
                      </Select>
                    </FormControl>

                    {/* Definition Selector - only show when a specification is selected */}
                    {importFormData.collection && codifyDefinitions.length > 0 && (
                      <FormControl>
                        <FormLabel>Definition</FormLabel>
                        <Select
                          size="sm"
                          value={selectedDefinition}
                          onChange={handleDefinitionChange}
                          placeholder="Select a definition...">
                          {codifyDefinitions
                            .sort((a, b) => a.type.localeCompare(b.type))
                            .map((def) => (
                              <option key={def._id} value={def._id}>
                                {def.type}
                              </option>
                            ))}
                        </Select>
                      </FormControl>
                    )}

                    {/* Show loading state while fetching definitions */}
                    {importFormData.collection && codifyDefinitions.length === 0 && (
                      <FormControl>
                        <FormLabel>Definition</FormLabel>
                        <Select size="sm" isDisabled placeholder="Loading definitions...">
                        </Select>
                      </FormControl>
                    )}
                  </HStack>

                  <Box
                    ref={dropZoneRef}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    border="2px dashed"
                    borderColor={isDragging ? "blue.500" : "gray.300"}
                    borderRadius="md"
                    p={6}
                    textAlign="center"
                    bg={isDragging ? "blue.50" : "white"}
                    transition="all 0.2s"
                    cursor="pointer"
                    _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                    onClick={() => fileInputRef.current?.click()}>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".txt,.text,.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.json,.xml,.csv"
                      onChange={handleFileSelect}
                      display="none"
                      id="register-file-upload"
                    />
                    <VStack spacing={2}>
                      <HiOutlineUpload size={32} color={isDragging ? "#3182CE" : "#A0AEC0"} />
                      <Text fontWeight="medium" color={isDragging ? "blue.600" : "gray.700"}>
                        {isDragging ? "Drop file here" : "Drag and drop a file here"}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        or
                      </Text>
                      <Button
                        leftIcon={<HiOutlineUpload />}
                        size="sm"
                        colorScheme="blue"
                        variant="outline">
                        Choose File
                      </Button>
                    </VStack>
                  </Box>

                  {selectedFile && (
                    <HStack spacing={2} p={3} bg="green.50" borderRadius="md" justify="space-between">
                      <HStack spacing={2}>
                        <Badge colorScheme="green" fontSize="sm">
                          {selectedFile.name}
                        </Badge>
                        <Badge colorScheme="blue" fontSize="sm">
                          {(selectedFile.size / 1024).toFixed(2)} KB
                        </Badge>
                      </HStack>
                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={handleImportFile}
                          isLoading={isImporting}
                          loadingText="Importing..."
                          isDisabled={isImporting || !selectedCodifySpec || !selectedDefinition || !importFormData.type}>
                          Import
                        </Button>
                        <IconButton
                          icon={<MdClose />}
                          size="sm"
                          colorScheme="red"
                          onClick={handleRemoveFile}
                          aria-label="Remove file"
                          isDisabled={isImporting}
                        />
                      </HStack>
                    </HStack>
                  )}

                  {importProgress && (
                    <Alert status={importProgress.status === "error" ? "error" : importProgress.status === "completed" ? "success" : "info"} borderRadius="md">
                      <AlertIcon />
                      <Box flex="1">
                        <AlertTitle>
                          {importProgress.status === "error" ? "Import Error" : importProgress.status === "completed" ? "Import Complete" : "Importing"}
                        </AlertTitle>
                        <AlertDescription display="block">
                          {importProgress.message}
                          {isImporting && (
                            <Progress size="xs" isIndeterminate mt={2} />
                          )}
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}

                  <Text fontSize="xs" color="gray.600">
                    Supported: .txt, .text, .pdf, .docx, .doc, .xlsx, .xls, .pptx, .ppt, .json, .xml, .csv (Max 10MB)
                  </Text>
                </VStack>
              </Box>

              {entriesLoading ? (
                <Spinner />
              ) : (
                <Box
                  className="ag-theme-alpine"
                  style={{ height: "400px", width: "100%" }}>
                  <AgGridReact
                    rowData={entries}
                    columnDefs={columnDefs}
                    defaultColDef={{
                      resizable: true,
                    }}
                    pagination={true}
                    paginationPageSize={10}
                    domLayout="normal"
                  />
                </Box>
              )}
            </Box>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default RegistersEditor;
